import { useEffect, useState } from 'react';

export default function HeroSection() {
  const [ripples, setRipples] = useState<{ id: number; delay: number }[]>([]);
  const [textVisible, setTextVisible] = useState(false);

  useEffect(() => {
    // 波紋を複数生成（時間差あり）
    const rippleList = [
      { id: 1, delay: 0 },
      { id: 2, delay: 2.0 },
      { id: 3, delay: 4.0 },
      { id: 4, delay: 6.0 },
    ];
    setRipples(rippleList);

    // 文字は少し遅れて浮かび上がる
    const timer = setTimeout(() => {
      setTextVisible(true);
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  const scrollToNext = () => {
    const element = document.getElementById('philosophy');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative h-screen w-full overflow-hidden">
      {/* Background Image with subtle scale animation */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/f4d61e2b56ecafac352304f35eda7bb8.jpg"
          alt="Japanese Archipelago"
          className="w-full h-full object-cover object-top"
          style={{
            animation: 'heroImagePulse 8s ease-in-out infinite',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
      </div>

      {/* Ripple Effects */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        {ripples.map((ripple) => (
          <div
            key={ripple.id}
            className="absolute rounded-full border border-white/20"
            style={{
              width: '10px',
              height: '10px',
              animation: `rippleExpand 8s ease-out infinite`,
              animationDelay: `${ripple.delay}s`,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative h-full flex flex-col items-center justify-center px-8">
        <div className="text-center w-full">
          {/* Main Japanese Copy - PC版 */}
          <h1
            className="hidden md:block font-serif text-6xl lg:text-7xl xl:text-8xl font-bold text-white mb-6 tracking-wide leading-tight"
            style={{
              opacity: textVisible ? 1 : 0,
              transform: textVisible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.97)',
              transition: 'opacity 2s ease-out, transform 2s ease-out',
              transitionDelay: '0s',
            }}
          >
            海流の律動、列島の旋律。
          </h1>
          {/* モバイル版 - 句読点で改行 */}
          <h1
            className="block md:hidden font-serif font-bold text-white mb-4 tracking-wide leading-snug"
            style={{
              opacity: textVisible ? 1 : 0,
              transform: textVisible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.97)',
              transition: 'opacity 2s ease-out, transform 2s ease-out',
              transitionDelay: '0s',
              fontSize: 'clamp(1.75rem, 8vw, 2.5rem)',
            }}
          >
            海流の律動、<br />列島の旋律。
          </h1>

          {/* Sub Japanese Copy - PC版 */}
          <p
            className="hidden md:block font-serif text-2xl lg:text-3xl text-white/95 mb-8 font-light tracking-wider"
            style={{
              opacity: textVisible ? 1 : 0,
              transform: textVisible ? 'translateY(0)' : 'translateY(16px)',
              transition: 'opacity 2s ease-out, transform 2s ease-out',
              transitionDelay: '0.6s',
            }}
          >
            島国の感性が紡ぐ、日本音楽の構造。
          </p>
          {/* Sub Japanese Copy - モバイル版 - 句読点で改行 */}
          <p
            className="block md:hidden font-serif text-white/95 mb-5 font-light tracking-wider leading-relaxed"
            style={{
              opacity: textVisible ? 1 : 0,
              transform: textVisible ? 'translateY(0)' : 'translateY(16px)',
              transition: 'opacity 2s ease-out, transform 2s ease-out',
              transitionDelay: '0.6s',
              fontSize: 'clamp(1rem, 4.5vw, 1.25rem)',
            }}
          >
            島国の感性が紡ぐ、<br />日本音楽の構造。
          </p>

          {/* English Copy */}
          <p
            className="font-sans text-white/80 tracking-widest font-light mx-auto leading-loose"
            style={{
              opacity: textVisible ? 1 : 0,
              transform: textVisible ? 'translateY(0)' : 'translateY(12px)',
              transition: 'opacity 2s ease-out, transform 2s ease-out',
              transitionDelay: '1.2s',
              fontSize: 'clamp(0.65rem, 2vw, 1rem)',
              maxWidth: '36rem',
            }}
          >
            The rhythm of the ocean currents,<br />
            the melody of the archipelago.<br />
            <span className="opacity-80">The Structure of Japanese Music<br className="block md:hidden" /> Born from Insular Sensibility.</span>
          </p>
        </div>
      </div>

      {/* Scroll Indicator */}
      <button
        onClick={scrollToNext}
        className="absolute bottom-12 left-1/2 -translate-x-1/2 text-white/70 hover:text-white transition-colors cursor-pointer animate-bounce"
        aria-label="Scroll to next section"
        style={{
          opacity: textVisible ? 1 : 0,
          transition: 'opacity 1.5s ease-out',
          transitionDelay: '2s',
        }}
      >
        <div className="w-6 h-6 flex items-center justify-center">
          <i className="ri-arrow-down-line text-2xl"></i>
        </div>
      </button>

      {/* Keyframe styles */}
      <style>{`
        @keyframes rippleExpand {
          0% {
            width: 10px;
            height: 10px;
            opacity: 0.5;
            border-color: rgba(255,255,255,0.35);
          }
          100% {
            width: 120vw;
            height: 120vw;
            opacity: 0;
            border-color: rgba(255,255,255,0);
          }
        }
        @keyframes heroImagePulse {
          0%, 100% {
            transform: scale(1.0);
          }
          50% {
            transform: scale(1.04);
          }
        }
      `}</style>
    </section>
  );
}
